import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useFeasibilityPlan } from '../hooks/useFeasibilityPlan';
import type { ExperimentFeasibilityPlan, FeasibilityPlanScope } from '../types';
import { EXPERIMENTS_TEST_IDS } from '../experimentsTestIds';

interface FeasibilityPanelProps {
  projectId: string;
  /** Scope of the linked A/B placement (alias) — project-wide when absent. */
  scope?: FeasibilityPlanScope;
  /** Writes the planner's recommended per-variant sample into the form field. */
  onUseMinimumSample: (samplePerVariant: number) => void;
}

const HORIZON_LABEL: Record<number, string> = {
  1: '1 week',
  2: '2 weeks',
  4: '4 weeks',
};

function formatPercent(value: number): string {
  const percent = value * 100;
  if (percent >= 100) return `${Math.round(percent)}%`;
  return `${percent.toFixed(1)}%`;
}

/** '' → undefined; percent text (e.g. "10") → relative fraction (0.1). */
function parseMdePercent(raw: string): number | undefined {
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) && value > 0 ? value / 100 : undefined;
}

/**
 * J1 — ADVISORY feasibility panel under the minimum-sample field: "with your
 * recent traffic and base rate you can detect ≥X% relative lift in 1/2/4
 * weeks", the inverse runtime for the lift you expect, or an honest
 * "not enough traffic history". Never blocks form submission.
 */
export const FeasibilityPanel: React.FC<FeasibilityPanelProps> = ({
  projectId,
  scope,
  onUseMinimumSample,
}) => {
  const [mdeInput, setMdeInput] = useState('');
  const targetMde = parseMdePercent(mdeInput);

  const { plan, loading, error, errorCopy, retry } = useFeasibilityPlan({
    projectId,
    enabled: Boolean(projectId),
    scope,
    // The desired detectable lift doubles as the expected lift, so the plan
    // carries both the required sample and the "~N weeks" runtime for it.
    overrides: targetMde !== undefined
      ? { targetMde, expectedRelativeLift: targetMde }
      : undefined,
  });

  return (
    <Stack
      spacing={1}
      data-testid={EXPERIMENTS_TEST_IDS.feasibilityPanel}
      sx={{ p: 1.25, borderRadius: 1, bgcolor: 'action.hover' }}
    >
      <Typography variant="caption" color="text.secondary">
        Feasibility check (advisory — based on recent traffic, never blocks saving)
      </Typography>
      <TextField
        label="Expected lift to detect (% relative)"
        size="small"
        type="number"
        placeholder="10"
        value={mdeInput}
        onChange={(event) => setMdeInput(event.target.value)}
        inputProps={{ 'data-testid': EXPERIMENTS_TEST_IDS.formMde, min: 0, step: 1 }}
        helperText="Optional: how small a relative lift do you want the test to detect?"
      />

      <div data-testid={EXPERIMENTS_TEST_IDS.feasibilityStatus}>
        {loading && (
          <Stack spacing={0.5}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary">
              Estimating from recent traffic…
            </Typography>
          </Stack>
        )}

        {!loading && error && (
          <Alert
            severity="warning"
            action={<Button size="small" onClick={retry}>Retry</Button>}
          >
            Feasibility estimate unavailable ({error}). Advisory only — you can still save the hypothesis.
            {errorCopy?.techDetails && (
              <Box component="details" data-testid={EXPERIMENTS_TEST_IDS.feasibilityTechDetails} sx={{ mt: 1 }}>
                <Box component="summary" sx={{ cursor: 'pointer' }}>Technical details</Box>
                <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', mt: 0.5, wordBreak: 'break-word' }}>
                  {errorCopy.techDetails}
                </Typography>
              </Box>
            )}
          </Alert>
        )}

        {!loading && !error && plan?.state === 'insufficient_history' && (
          <Alert severity="info">
            Not enough traffic history in this scope to estimate feasibility honestly.
            Consider a{' '}
            <Link component={RouterLink} to={`/project/${projectId}/launch`}>
              safe rollout
            </Link>{' '}
            or a much bolder variant instead of an A/B test,
            and revisit once traffic accumulates.
          </Alert>
        )}

        {!loading && !error && plan?.state === 'ok' && (
          <PlanBody plan={plan} onUseMinimumSample={onUseMinimumSample} />
        )}
      </div>
    </Stack>
  );
};

const PlanBody: React.FC<{
  plan: ExperimentFeasibilityPlan;
  onUseMinimumSample: (samplePerVariant: number) => void;
}> = ({ plan, onUseMinimumSample }) => (
  <Stack spacing={0.5}>
    <Typography variant="caption" color="text.secondary">
      ~{Math.round(plan.observed.weeklyTraffic).toLocaleString()} journeys/week,
      base rate {plan.observed.baseRate === null ? 'n/a' : formatPercent(plan.observed.baseRate)}
      {' '}(last {plan.observed.windowDays} days{plan.observed.source !== 'observed' ? ', with overrides' : ''})
    </Typography>

    {(plan.horizons ?? []).map(horizon => (
      <Typography
        key={horizon.weeks}
        variant="body2"
        data-testid={EXPERIMENTS_TEST_IDS.feasibilityHorizon(horizon.weeks)}
      >
        {horizon.achievableRelativeMde === null
          ? `${HORIZON_LABEL[horizon.weeks]}: no detectable lift at this traffic`
          : `${HORIZON_LABEL[horizon.weeks]}: detects ≥${formatPercent(horizon.achievableRelativeMde)} relative lift`}
      </Typography>
    ))}

    {plan.runtimeForExpectedLift && (
      <Typography variant="body2">
        Your expected {formatPercent(plan.runtimeForExpectedLift.relativeLift)} lift needs
        ~{plan.runtimeForExpectedLift.weeks} week{plan.runtimeForExpectedLift.weeks === 1 ? '' : 's'}
        {' '}({plan.runtimeForExpectedLift.requiredSamplePerVariant.toLocaleString()} users/variant).
      </Typography>
    )}

    <Typography
      variant="caption"
      color={plan.recommendation === 'ab_test' ? 'success.main' : 'warning.main'}
      data-testid={EXPERIMENTS_TEST_IDS.feasibilityRecommendation}
    >
      {plan.recommendation === 'ab_test'
        ? 'An A/B test is feasible for lifts within the ranges above.'
        : 'Even 4 weeks only detects a >30% relative lift here — prefer a safe rollout or a much bolder variant.'}
    </Typography>

    {plan.messages.map((message, index) => (
      <Typography key={index} variant="caption" color="text.secondary">
        {message}
      </Typography>
    ))}

    <div>
      <Button
        size="small"
        disabled={plan.recommendedMinimumSamplePerVariant === undefined}
        data-testid={EXPERIMENTS_TEST_IDS.feasibilityUseMde}
        onClick={() => {
          if (plan.recommendedMinimumSamplePerVariant !== undefined) {
            onUseMinimumSample(plan.recommendedMinimumSamplePerVariant);
          }
        }}
      >
        Use as minimum sample
      </Button>
    </div>
  </Stack>
);
